import {
  parsearFecha,
  parsearMonto,
  montosEnLinea,
  limpiarConcepto,
  fechaDesdePartes,
  round2,
  crearFiltroPagina,
  armarDetalle,
} from "./helpers";

/**
 * Plantilla de BBVA (BBVA México / Bancomer).
 *
 * Formato de "Detalle de Movimientos Realizados":
 *   FECHA(OPER LIQ) | COD. | DESCRIPCIÓN | REFERENCIA | CARGOS | ABONOS | SALDO(OPERACIÓN LIQUIDACIÓN)
 *
 * Retos propios de BBVA (el formato más complejo):
 *  - Columnas CARGOS y ABONOS SEPARADAS que se colapsan al extraer texto, así que
 *    no se puede saber por posición si un importe es cargo o abono.
 *  - El SALDO sólo aparece en ALGUNOS renglones (checkpoints), no en todos, por lo
 *    que el truco del saldo corriente no sirve renglón por renglón.
 *  - Dos columnas de fecha (OPER/LIQ) y dos de saldo (OPERACIÓN/LIQUIDACIÓN).
 *  - Descripciones de varias líneas.
 *
 * Estrategia (validada contra los totales oficiales del estado de cuenta):
 *  - El MONTO siempre es el PRIMER importe del renglón (la columna CARGOS o ABONOS,
 *    la que esté llena; son excluyentes).
 *  - El TIPO se deduce por palabra clave: "depósito/recibido/dep" = entra (cargo);
 *    el resto (pagos, comisiones, IVA, cheque pagado, IMSS...) = sale (abono).
 *    Nota: en el ERP cargo = depósito (entra) y abono = pago (sale).
 *  - La fecha es la de OPERación ("DD/MMM"); el año viene del período del encabezado.
 */

// Renglón de movimiento: "OPER LIQ [COD] ..." (dos fechas DD/MMM; la columna de
// código de 2-3 chars es OPCIONAL: aparece en el formato empresarial pero NO en
// otros formatos como "Libretón Básico Cuenta Digital", donde tras las fechas
// viene directo la descripción. El único requisito firme son las dos fechas.
const RE_MOV_INICIO = /^(\d{1,2})\/([A-Za-zÁÉÍÓÚáéíóú]{3})\s+\d{1,2}\/[A-Za-zÁÉÍÓÚáéíóú]{3}(?:\s+[A-Z0-9]{2,3}\b)?/;
const RE_DETALLE = /detalle de movimientos/i;
const RE_FIN = /total de movimientos|total importe/i;
// Saldo anterior, para arrancar la cadena de saldos y deducir el tipo por delta.
const RE_SALDO_ANTERIOR = /saldo\s+anterior\b[^$\d-]*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i;
const RE_ENTRADA = /dep[oó]sito|recibido|\bdep\b|dep\.|n[oó]mina|devoluci[oó]n/i; // entra (cargo)
const RE_PRIMER_MONTO = /-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}/;

const bbva = {
  id: "bbva",
  nombre: "BBVA",
  clavesSat: ["012"],
  // Detección por la identidad PROPIA de BBVA (RFC / razón social), no por "BBVA"
  // como contraparte de un SPEI en el estado de cuenta de otro banco.
  detectar: (texto) => /bba830831lj2|grupo financiero bbva|bbva m[eé]xico,?\s*s\.?a\.?/i.test(texto),
  // Encabezado: "Periodo DEL 01/05/2026 AL 31/05/2026" + "Saldo Final (+) 38,307.69".
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(/periodo\s+del\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\s+al\s+(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
    const sf = t.match(/saldo\s+final\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? parsearFecha(per[1], null, ["DD/MM/YYYY", "DD/MM/YY"]) : null,
      fechaFinal: per ? parsearFecha(per[2], null, ["DD/MM/YYYY", "DD/MM/YY"]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // Acotar a la sección de movimientos (de "Detalle de Movimientos" a "Total...").
    let ini = arr.findIndex((l) => RE_DETALLE.test(l));
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_FIN.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);
    const idxPrimerMov = seccion.findIndex((l) => RE_MOV_INICIO.test(l));
    const esDePagina = crearFiltroPagina(arr, idxPrimerMov > -1 ? ini + 1 + idxPrimerMov : -1);

    // Año del período (las fechas de renglón sólo traen "DD/MMM").
    const perTxt = arr.join("\n").match(/periodo\s+del\s+\d{1,2}\/\d{1,2}\/(\d{2,4})/i);
    let anioPeriodo = perTxt ? parseInt(perTxt[1], 10) : anioRef;
    if (perTxt && perTxt[1].length === 2) anioPeriodo = 2000 + parseInt(perTxt[1], 10);

    // Saldo de arranque para deducir el tipo por la dirección del delta del saldo.
    const mSaldoAnt = arr.join("\n").match(RE_SALDO_ANTERIOR);
    let prevSaldo = mSaldoAnt ? parsearMonto(mSaldoAnt[1]) : null;
    // ¿El saldo anterior viene del renglón inmediatamente previo? Sólo entonces el
    // delta corresponde a UN movimiento; si hubo renglones sin saldo en medio, el
    // delta abarcaría varios y no es confiable, así que ahí caemos a palabra clave.
    let saldoPrevDirecto = prevSaldo != null;

    const movimientos = [];
    for (let i = 0; i < seccion.length; i++) {
      const linea = seccion[i];
      const mMov = linea.match(RE_MOV_INICIO);
      if (!mMov) continue;

      const ms = montosEnLinea(linea);
      if (ms.length === 0) continue;

      const monto = Math.abs(ms[0]); // primer importe = movimiento (cargo o abono)
      const fecha = fechaDesdePartes(mMov[1], mMov[2], anioPeriodo);

      //  Si no hay saldo en el renglón, se cae a la palabra clave de la descripción.
      const saldoRenglon = ms.length >= 2 ? ms[1] : null;
      let tipo;
      if (saldoRenglon != null && prevSaldo != null && saldoPrevDirecto) {
        tipo = round2(saldoRenglon - prevSaldo) >= 0 ? "cargo" : "abono";
      } else {
        tipo = RE_ENTRADA.test(linea) ? "cargo" : "abono";
      }
      // Avanza la cadena de saldos: si este renglón trae saldo, el siguiente puede
      // usar su delta; si no, marca que el próximo no tiene saldo directo previo.
      if (saldoRenglon != null) {
        prevSaldo = saldoRenglon;
        saldoPrevDirecto = true;
      } else {
        saldoPrevDirecto = false;
      }

      // Concepto: texto tras OPER/LIQ/COD y antes del primer importe; se enriquece
      // con la línea de detalle siguiente (referencia) si la hay.
      const idxMonto = linea.search(RE_PRIMER_MONTO);
      let concepto = limpiarConcepto(linea.slice(mMov[0].length, idxMonto > -1 ? idxMonto : undefined));
      const sig = seccion[i + 1];
      if (sig && !RE_MOV_INICIO.test(sig) && !RE_FIN.test(sig)) {
        concepto = limpiarConcepto(concepto + " " + sig);
      }

      // Detalle: todas las líneas hasta el siguiente movimiento (sin encabezados/pies).
      const extras = [];
      for (let j = i + 1; j < seccion.length && !RE_MOV_INICIO.test(seccion[j]); j++) {
        if (!esDePagina(seccion[j])) extras.push(seccion[j]);
      }

      movimientos.push({
        fecha,
        monto,
        tipo,
        concepto,
        detalle: armarDetalle([linea.slice(mMov[0].length, idxMonto > -1 ? idxMonto : undefined), ...extras]),
        raw: linea,
        rawExtra: extras.join(" "),
      });
    }

    return movimientos;
  },
};

export default bbva;
